import { useCallback, useEffect, useRef, useState } from 'react';
import { RoomApiError, getRoomToken, roomFetch, setRoomToken } from './roomFetch';

/**
 * Autosave for the Room wizard.
 *
 * The shape of it, and why:
 *  - Save on field BLUR, sending only DIRTY fields. No periodic timer — a timer either saves too
 *    often (every keystroke) or loses the last edit; blur is when a field is actually done.
 *  - Flush on tab hide / close (`visibilitychange`→hidden, `pagehide`) so an answer typed and then
 *    abandoned still lands. This flush uses a keepalive fetch, NOT `navigator.sendBeacon`: beacon
 *    cannot set the `Authorization` header the Room session needs.
 *  - Retry transient failures (network / 5xx) a couple of times; never retry a 4xx (validation/auth
 *    won't improve on repeat). Keep unsaved fields buffered so the next blur or flush tries again.
 *  - Refresh the room JWT just before it expires, so a client filling a long form is never bounced
 *    to re-verify mid-answer.
 *
 * "Empty" on the wire is `null` (the agreed representation): an emptied field sends `null`, which the
 * server and engine both read as empty. The wizard keeps `""` locally for the controlled input.
 */

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/** Refresh the token when it has less than this left — comfortably inside its ~2h life. */
const REFRESH_BEFORE_EXPIRY_MS = 10 * 60 * 1000;
const MAX_RETRIES = 2;
const RETRY_BASE_MS = 300;

/**
 * Read a JWT's `exp` (seconds) without verifying it — verification is the server's job; the client
 * only needs to know WHEN to refresh. Returns null for anything it can't read.
 */
export function decodeJwtExp(token: string): number | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const parsed = JSON.parse(atob(padded)) as { exp?: unknown };
    return typeof parsed.exp === 'number' ? parsed.exp : null;
  } catch {
    return null;
  }
}

/** If the stored token is near expiry, slide it. Best-effort — a failure surfaces later as a 401. */
async function ensureFreshToken(): Promise<void> {
  const token = getRoomToken();
  if (!token) return;
  const exp = decodeJwtExp(token);
  if (exp === null) return;
  if (exp * 1000 - Date.now() > REFRESH_BEFORE_EXPIRY_MS) return;

  try {
    const { token: fresh } = await roomFetch<{ token: string }>('room/session/refresh', { authenticated: true });
    setRoomToken(fresh);
  } catch {
    /* leave the old token; the imminent save will 401 and the wizard re-gates */
  }
}

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

export interface Autosave {
  status: SaveStatus;
  /** Buffer a changed field. `""` is normalised to `null` for the wire. */
  stage: (fieldId: string, rawValue: string) => void;
  /** Persist all buffered fields now, fire-and-forget (call on blur). */
  flush: () => void;
  /**
   * Persist all buffered fields and AWAIT completion — used before submitting the form, so the
   * server's authoritative completion check runs against answers that actually landed. Coalesces
   * with any in-flight save, and makes a second pass for a field edited during the round trip.
   */
  saveNow: () => Promise<void>;
}

export function useAutosave(): Autosave {
  const [status, setStatus] = useState<SaveStatus>('idle');
  const pending = useRef<Map<string, string | number | null>>(new Map());
  /** The in-flight save, so a blur and a submit racing coalesce onto one request instead of two. */
  const running = useRef<Promise<void> | null>(null);

  const stage = useCallback((fieldId: string, rawValue: string) => {
    pending.current.set(fieldId, rawValue === '' ? null : rawValue);
  }, []);

  const doSave = useCallback(async () => {
    setStatus('saving');
    await ensureFreshToken();

    const batch = Object.fromEntries(pending.current);
    for (let attempt = 0; ; attempt += 1) {
      try {
        await roomFetch('room/answers', { authenticated: true, body: { answers: batch } });
        // Clear only the fields we sent that haven't been re-edited since — a change during the
        // round trip stays dirty and saves on the next flush.
        for (const [id, value] of Object.entries(batch)) {
          if (pending.current.get(id) === value) pending.current.delete(id);
        }
        setStatus('saved');
        return;
      } catch (err) {
        const transient = !(err instanceof RoomApiError) || err.status === 0 || err.status >= 500;
        if (!transient || attempt >= MAX_RETRIES) {
          setStatus('error'); // pending is kept, so the next blur/flush retries
          return;
        }
        await delay(RETRY_BASE_MS * (attempt + 1));
      }
    }
  }, []);

  const save = useCallback(async (): Promise<void> => {
    if (running.current) return running.current; // coalesce concurrent callers
    if (pending.current.size === 0) return;
    const p = doSave().finally(() => {
      running.current = null;
    });
    running.current = p;
    return p;
  }, [doSave]);

  const flush = useCallback(() => {
    void save();
  }, [save]);

  const saveNow = useCallback(async () => {
    await save();
    // A field edited mid-save stays pending; one more pass covers the common case before submit.
    if (pending.current.size > 0) await save();
  }, [save]);

  // Tab hide / close: fire the buffered fields with keepalive so they survive unload. No retry, no
  // refresh, no status — the page is going away and none of that can run.
  useEffect(() => {
    const flushKeepalive = () => {
      if (pending.current.size === 0) return;
      const batch = Object.fromEntries(pending.current);
      void roomFetch('room/answers', { authenticated: true, body: { answers: batch }, keepalive: true }).catch(() => {});
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushKeepalive();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flushKeepalive);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flushKeepalive);
    };
  }, []);

  return { status, stage, flush, saveNow };
}
